<template>
  <div class="home-details">
    <FormHeading
      :ordinal="`0${stepNumber}.`"
      :title="step.heading"
      :subtitle="step.subheading"
    />
    <Select
      name="area"
      :options="options.area"
      :placeholder="$t('common.fieldPlaceholders.select')"
      :label="getFieldLabel('area')"
      :initialValue="initialValues.area"
    />
    <Select
      v-for="roomName in regularRooms"
      :key="roomName"
      :name="`spec.${roomName}`"
      :options="options[roomName]"
      :placeholder="$t('common.fieldPlaceholders.select')"
      :label="getFieldLabel(roomName)"
      :initialValue="initialValues.spec[roomName]"
    />
    <div class="home-details__row home-details__row--pets">
      <Select
        name="spec.pets"
        :options="options.pets"
        :placeholder="$t('common.fieldPlaceholders.select')"
        :label="getFieldLabel('pets')"
        :initialValue="initialValues.spec.pets"
      />
      <Input
        name="spec.pets_types"
        :label="getFieldLabel('pets_types')"
        :placeholder="$t('homeDetails.fieldPlaceholders.pets_types')"
        :initialValue="initialValues.spec.pets_types"
      />
    </div>
    <Select
      v-for="roomName in extraRooms"
      :key="roomName"
      :name="`spec.${roomName}`"
      :options="options[roomName]"
      :placeholder="$t('common.fieldPlaceholders.select')"
      :label="getFieldLabel(roomName)"
      :initialValue="initialValues.spec[roomName]"
    />
    <Input
      name="spec.other_area"
      inputType="number"
      :label="getFieldLabel('other')"
      :placeholder="$t('common.fieldPlaceholders.type_minutes')"
      :initialValue="initialValues.spec.other_area"
    />
    <Select
      name="spec.extra_time"
      :options="options.extra"
      :placeholder="$t('common.fieldPlaceholders.select')"
      :label="getFieldLabel('extra')"
      :initialValue="initialValues.spec.extra_time"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import estimateFormMixin from '@/common/mixins/estimate-form';
import FormHeading from '@/components/FormHeading';
import { isShape } from '@/common/prop-validators';
import { Input, Select } from '@/components/common';
import { formatOptions } from '@/common/helpers';

const { mapState } = createNamespacedHelpers('estimate');

const extraRooms = [
  'bonus_room',
  'loft',
  'office',
  'sunroom',
  'study',
  'basement',
];

const regularRooms = [
  'stories',
  'master_bath',
  'full_baths',
  'half_bath',
  'kitchen',
  'family_room',
  'dining_room',
  'living_room',
  'den',
  'master_bedroom',
  'bedrooms',
];

export default {
  name: 'HomeAreasForm',
  components: { Input, Select, FormHeading },
  mixins: [estimateFormMixin],
  data() {
    return {
      regularRooms,
      extraRooms,
    };
  },
  computed: {
    ...mapState({
      initialValues(state) {
        const defaultSpec = {
          extra_time: this.options.extra[0].value,
        };
        /**
         * @fixme This is a quick, dirty hack to set default area value by finding an option with
         * particular label. This should be fixed once time settings are refactored (they should
         * treat label as an identifier for an option, instead of value).
         */
        const defaultArea = this.options.area.find(opt => opt.label.includes('2001 - 2500')).value;
        const area = state.current.data.area || defaultArea;
        const spec = state.current.data.spec || defaultSpec;
        return { spec, area };
      },
    }),
    options() {
      const allFields = [...regularRooms, ...extraRooms, 'pets', 'extra'];
      return allFields.reduce((acc, field) => {
        acc[field] = formatOptions(this.settings.data.spec[field]);
        return acc;
      }, { area: formatOptions(this.settings.data.area) });
    },
  },
  props: {
    step: {
      type: Object,
      validator: isShape({
        component: Object,
        label: String,
        heading: String,
        id: String,
        subheading: String,
      }),
    },
    stepNumber: {
      type: Number,
    },
  },
  methods: {
    getFieldLabel(field) {
      return this.$t(`homeDetails.fieldLabels.${field}`);
    },
  },
};
</script>

<style lang="scss">
  .home-details {
    width: 100%;

    &__inputs {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      margin-bottom: 8px;

      @include md-up {
        margin-bottom: 24px;
      }
    }

    &__row {
      &--pets {
        @include md-up {
          align-items: flex-start;
          display: flex;
          justify-content: space-between;

          >.ant-form-item {
            flex: 0 0 calc(50% - 8px);
          }

          .input {
            .ant-input-affix-wrapper {
              margin: 4px 0 0;
            }
          }
        }
      }
    }
  }
</style>
